import React, { Component } from 'react';
import {
	Grid,
	GridItem,
	Label,
	TagButton,
	VisuallyHidden,
} from '../../../../ui';
import styled from '@emotion/styled';
import { inject, observer } from 'mobx-react';
import { FlightScopeButtons } from './FlightScopeButtons';
import { FlightDateSelect, ISelectDay } from './FlightDateSelect';
import { FlightSearchBar } from './FlightSearchBar';
import { II18nProps, withTranslate } from '../../../shared/i18n/translate';
import { FlightListScope } from '../../overview/domain/constants/FlightListScope';
import { IFlightSearchSuggestion } from '../domain/models/IFlightSearchSuggestion';
import { FlightListStore } from '../../overview/domain/stores';
import { IStores } from '../../../app/root/domain/stores/createStores';
import Responsive from '../../../../ui/utils/Responsive';

const StyledFlightSearchMain = styled(Grid)`
	margin-bottom: 32px;

	${Label} {
		position: initial;
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		margin-bottom: 24px;
	}
`;

interface IProps {
	currentScope: FlightListScope;
	onChangeDate: (date: ISelectDay | null) => void;
	onChangeScope: (scope: FlightListScope) => void;
	onSelectFlightSuggestion?: (
		selectedItem: IFlightSearchSuggestion | null,
	) => void;
}

interface IInjectedProps extends IProps {
	flightListStore: FlightListStore;
}

interface IState {
	isMounted: boolean;
}

const FlightSearchWithI18n = inject(({ flightListStore }: IStores) => ({
	flightListStore,
}))(
	observer(
		class FlightSearchWithI18n extends Component<IProps & II18nProps, IState> {
			public state: IState = {
				isMounted: false,
			};

			protected get injected() {
				return this.props as IInjectedProps & II18nProps;
			}

			public componentDidMount() {
				this.setState({ isMounted: true });
			}

			protected onDateSelectChange = (selected: ISelectDay | null) => {
				const { onChangeDate } = this.props;

				onChangeDate(selected);
			};

			protected onClickAll = () => {
				const { onChangeScope } = this.props;

				onChangeScope(FlightListScope.All);
			};

			protected onClickDepartures = () => {
				const { onChangeScope } = this.props;

				onChangeScope(FlightListScope.Departures);
			};

			protected onClickArrivals = () => {
				const { onChangeScope } = this.props;

				onChangeScope(FlightListScope.Arrivals);
			};

			protected onQuerySubmit = (query: string) => {
				this.injected.flightListStore.setQuery(query);
			};

			public render() {
				const { t, currentScope, onSelectFlightSuggestion } = this.props;
				const { finishedInitialLoad } = this.injected.flightListStore;
				const { isMounted } = this.state;

				return (
					<>
						<StyledFlightSearchMain>
							<GridItem columns={[3, { mobileXL: 12, tablet: 4 }]}>
								<FlightDateSelect
									onChange={this.onDateSelectChange}
									disabled={!isMounted}
								/>
							</GridItem>

							<GridItem columns={[9, { mobileXL: 12, tablet: 8 }]}>
								<FlightSearchBar
									onSelectItem={onSelectFlightSuggestion}
									onQuerySubmit={this.onQuerySubmit}
									disabled={!finishedInitialLoad}
								/>
							</GridItem>
						</StyledFlightSearchMain>

						<FlightScopeButtons
							role="group"
							aria-label="flight options, flight results will be updated based on your selection"
						>
							<TagButton
								onClick={this.onClickAll}
								active={currentScope === FlightListScope.All}
								disabled={!finishedInitialLoad}
								aria-current={
									currentScope === FlightListScope.All ? 'true' : undefined
								}
							>
								{t('flight_list_filter_all')}
								{currentScope === FlightListScope.All && (
									<VisuallyHidden> (selected)</VisuallyHidden>
								)}
							</TagButton>

							<TagButton
								onClick={this.onClickDepartures}
								active={currentScope === FlightListScope.Departures}
								disabled={!finishedInitialLoad}
								aria-current={
									currentScope === FlightListScope.Departures
										? 'true'
										: undefined
								}
							>
								{t('flight_list_filter_departures')}
								{currentScope === FlightListScope.Departures && (
									<VisuallyHidden> (selected)</VisuallyHidden>
								)}
							</TagButton>

							<TagButton
								onClick={this.onClickArrivals}
								active={currentScope === FlightListScope.Arrivals}
								aria-current={
									currentScope === FlightListScope.Arrivals ? 'true' : undefined
								}
								disabled={!finishedInitialLoad}
							>
								{t('flight_list_filter_arrivals')}
								{currentScope === FlightListScope.Arrivals && (
									<VisuallyHidden> (selected)</VisuallyHidden>
								)}
							</TagButton>
						</FlightScopeButtons>
					</>
				);
			}
		},
	),
);

export const FlightSearch = withTranslate<IProps>(FlightSearchWithI18n);
